import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import AuthGuard from './components/AuthGuard';
import RoleGuard from './components/RoleGuard';
import ErrorBoundary from './components/ErrorBoundary';
import 'react-toastify/dist/ReactToastify.css';

// Lazy load pages for code splitting
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Identification = lazy(() => import('./pages/Identification'));
const CampaignList = lazy(() => import('./pages/CampaignList'));
const CampaignLaunch = lazy(() => import('./pages/CampaignLaunch'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const ApiKeyManagement = lazy(() => import('./pages/ApiKeyManagement'));

// Loading component
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<Navigate to="/campaign-list" />} />
      
      {/* Identification - Admin and Editor only */}
      <Route
        path="/identification"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['admin', 'editor']}>
              <Layout>
                <Identification />
              </Layout>
            </RoleGuard>
          </AuthGuard>
        }
      />
      
      {/* Campaign List - All roles */}
      <Route
        path="/campaign-list"
        element={
          <AuthGuard>
            <Layout>
              <CampaignList />
            </Layout>
          </AuthGuard>
        }
      />
      
      {/* Campaign Launch - Admin and Approver only */}
      <Route
        path="/campaign-launch"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['admin', 'approver']}>
              <Layout>
                <CampaignLaunch />
              </Layout>
            </RoleGuard>
          </AuthGuard>
        }
      />
      
      {/* Change Password - All roles */}
      <Route
        path="/change-password"
        element={
          <AuthGuard>
            <Layout>
              <ChangePassword />
            </Layout>
          </AuthGuard>
        }
      />
      
      {/* User Management - Admin only */}
      <Route
        path="/user-management"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['admin']}>
              <Layout>
                <UserManagement />
              </Layout>
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* API Key Management - Admin only */}
      <Route
        path="/api-key-management"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['admin']}>
              <Layout>
                <ApiKeyManagement />
              </Layout>
            </RoleGuard>
          </AuthGuard>
        }
      />
      
        <Route path="/" element={<Navigate to="/campaign-list" />} />
      </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppRoutes />
          <ToastContainer />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;